/**
 * App-level feature kill switches (client-side, temporary gates).
 *
 * These are intentionally simple boolean constants — NOT runtime config — so
 * that temporarily-disabled features have a single, discoverable flip point.
 * Re-enabling a feature = set its constant back to `true`.
 *
 * See zcode/14-OPEN-TASKS-TRACKER.md for the rationale of each gate.
 */

/**
 * POST_SALE_FOLLOWUP_ENABLED
 *
 * Temporarily DISABLED (false). Hides the entire Post-Sale feature across the
 * app — the developer dashboard "Post-Sale Follow-Up" section, the lead-profile
 * "Post-sale servicing" section, and the reservations follow-up panel — and
 * stops the corresponding API calls (`/post-sale/summary`, `/post-sale/follow-up`).
 *
 * The backend routes, DB migrations (128), and `post_sale:*` / `documents.*`
 * permissions are intentionally KEPT intact. Re-enabling later is a single flip
 * of this constant back to `true` (the existing per-user
 * `post_sale_follow_up_enabled` feature flag is then honored again).
 *
 * Tracker ref: zcode/14-OPEN-TASKS-TRACKER.md → n_hidepostsales.
 */
export const POST_SALE_FOLLOWUP_ENABLED = false;

/**
 * Resolve the post-sale follow-up visibility for a user.
 *
 * Combines the app-level kill switch with the per-user feature flag
 * (`user.feature_flags.post_sale_follow_up_enabled`, defaulting to `true`).
 * Returns `false` whenever the kill switch is off, regardless of the user flag.
 */
export function resolvePostSaleFollowUpEnabled(userFeatureFlag: unknown): boolean {
    if (!POST_SALE_FOLLOWUP_ENABLED) {
        return false;
    }
    return Boolean((userFeatureFlag as boolean | undefined | null) ?? true);
}
